import AddIcon from "@mui/icons-material/Add";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  addStore,
  deleteStore,
  fetchAllStores,
  updateStore,
} from "../../services/inventoryService";
import { RootState } from "../../store";
interface Store {
  store_id: string;
  name: string;
  address: string;
  city: string;
  postal_code: string;
  type: string;
  manager: string;
  email: string;
  phone: string;
}

type StoreForm = Partial<Store>;
type StoreAction = (store: Store) => void;

const EMPTY_STORE: StoreForm = {
  name: "",
  address: "",
  city: "",
  postal_code: "",
  type: "",
  manager: "",
  email: "",
  phone: "",
};

const TABLE_HEADINGS = [
  "Name",
  "Address",
  "City",
  "Postal Code",
  "Type",
  "Manager",
  "Email",
  "Phone",
  "Actions",
] as const;

const RESTRICTED_STORE_TYPES = new Set(["WAREHOUSE", "CLIENT"]);

const tableContainerSx = {
  maxHeight: "calc(100vh - 160px)",
  borderRadius: 2,
  overflow: "auto",
};

const tableHeaderCellSx = {
  bgcolor: `primary.main`,
  color: "white",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const actionIconButtonSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
};

const cardGridSx = {
  display: "grid",
  gap: 1.5,
  maxHeight: "calc(100vh - 230px)",
  overflow: "auto",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
  },
};

const cardSx = {
  p: 2,
  borderRadius: 2,
  minWidth: 0,
};

const storeIconSx = {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 1.5,
  bgcolor: "action.hover",
  display: "grid",
  placeItems: "center",
  color: "primary.contrastText",
};
const LOCATION_TYPES = [
  "STORE",
  "WAREHOUSE",
  "OTHER",
  "SUPPLIER",
  "CLIENT",
] as const;

type LocationType = (typeof LOCATION_TYPES)[number];

interface LocationTypeConfig {
  label: string;
  icon: React.ElementType;
}

const LOCATION_TYPE_CONFIG: Record<LocationType, LocationTypeConfig> = {
  STORE: {
    label: "Store",
    icon: StorefrontOutlinedIcon,
  },
  WAREHOUSE: {
    label: "Warehouse",
    icon: WarehouseOutlinedIcon,
  },
  OTHER: {
    label: "Other",
    icon: HelpOutlineOutlinedIcon,
  },
  SUPPLIER: {
    label: "Supplier",
    icon: LocalShippingOutlinedIcon,
  },
  CLIENT: {
    label: "Client",
    icon: BusinessOutlinedIcon,
  },
};

const isStoreFormComplete = (store: StoreForm): boolean =>
  Boolean(
    store.name?.trim() &&
    store.address?.trim() &&
    store.city?.trim() &&
    store.postal_code?.trim() &&
    store.type?.trim() &&
    store.manager?.trim() &&
    store.email?.trim() &&
    store.phone?.trim(),
  );

interface StoreInfoRowProps {
  icon: React.ReactNode;
  label: string;
  value?: string;
}

const StoreInfoRow = memo(function StoreInfoRow({
  icon,
  label,
  value,
}: StoreInfoRowProps) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{
          display: "flex",
          color: "text.secondary",
          mt: "2px",
          "& svg": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflowWrap: "anywhere",
          }}
        >
          {value || "—"}
        </Typography>
      </Box>
    </Stack>
  );
});

interface StoreTableRowProps {
  store: Store;
  canEdit: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  onEdit: StoreAction;
  onDelete: StoreAction;
}

const StoreTableRow = memo(function StoreTableRow({
  store,
  canEdit,
  canDelete,
  isDeleting,
  onEdit,
  onDelete,
}: StoreTableRowProps) {
  const handleEdit = useCallback(() => onEdit(store), [onEdit, store]);
  const handleDelete = useCallback(() => onDelete(store), [onDelete, store]);

  return (
    <TableRow hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
      <TableCell sx={{ fontWeight: 600 }}>{store.name}</TableCell>
      <TableCell sx={{ minWidth: 190 }}>{store.address}</TableCell>
      <TableCell>{store.city}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{store.postal_code}</TableCell>
      <TableCell>
        <Chip label={store.type} size="small" variant="outlined" />
      </TableCell>
      <TableCell>{store.manager}</TableCell>
      <TableCell sx={{ minWidth: 190 }}>{store.email}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{store.phone}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={handleEdit}
            disabled={!canEdit}
          >
            Edit
          </Button>
          <IconButton
            size="small"
            color="error"
            aria-label={`Delete ${store.name}`}
            onClick={handleDelete}
            disabled={!canDelete || isDeleting}
            sx={actionIconButtonSx}
          >
            {isDeleting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DeleteOutlineIcon fontSize="small" />
            )}
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
});

interface StoreCardProps extends StoreTableRowProps {}

const StoreCard = memo(function StoreCard({
  store,
  canEdit,
  canDelete,
  isDeleting,
  onEdit,
  onDelete,
}: StoreCardProps) {
  const handleEdit = useCallback(() => onEdit(store), [onEdit, store]);
  const handleDelete = useCallback(() => onDelete(store), [onDelete, store]);

  const fullAddress = useMemo(
    () => `${store.address}, ${store.city}, ${store.postal_code}`,
    [store.address, store.city, store.postal_code],
  );
  const locationConfig = LOCATION_TYPE_CONFIG[store.type as LocationType];
  const LocationIcon = locationConfig.icon;
  return (
    <Paper variant="outlined" sx={cardSx}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Stack direction="row" spacing={1.25} minWidth={0}>
          <Box sx={storeIconSx}>
            <StorefrontOutlinedIcon />
          </Box>
          <Box minWidth={0}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              noWrap
              title={store.name}
            >
              {store.name}
            </Typography>
            {/* <Chip
              label={store.type}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5 }}
            /> */}
            <Chip
              icon={<LocationIcon />}
              label={locationConfig.label}
              size="small"
              variant="outlined"
            />
          </Box>
        </Stack>
      </Stack>

      <Divider sx={{ my: 1.75 }} />

      <Stack spacing={1.5}>
        <StoreInfoRow
          icon={<LocationOnOutlinedIcon />}
          label="Address"
          value={fullAddress}
        />
        <StoreInfoRow
          icon={<PersonOutlineIcon />}
          label="Manager"
          value={store.manager}
        />
        <StoreInfoRow
          icon={<EmailOutlinedIcon />}
          label="Email"
          value={store.email}
        />
        <StoreInfoRow
          icon={<PhoneOutlinedIcon />}
          label="Phone"
          value={store.phone}
        />
      </Stack>

      <Divider sx={{ my: 1.75 }} />

      <Stack direction="row" spacing={1}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<EditOutlinedIcon />}
          onClick={handleEdit}
          disabled={!canEdit}
        >
          Edit
        </Button>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          color="error"
          startIcon={
            isDeleting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlineIcon />
            )
          }
          onClick={handleDelete}
          disabled={!canDelete || isDeleting}
        >
          Delete
        </Button>
      </Stack>
    </Paper>
  );
});

interface StoreListProps {
  stores: Store[];
  isSuperAdmin: boolean;
  deletingStoreId: string | null;
  onEdit: StoreAction;
  onDelete: StoreAction;
}

const StoreTable = memo(function StoreTable({
  stores,
  isSuperAdmin,
  deletingStoreId,
  onEdit,
  onDelete,
}: StoreListProps) {
  const rows = useMemo(
    () =>
      stores.map((store) => (
        <StoreTableRow
          key={store.store_id}
          store={store}
          canEdit={
            isSuperAdmin ||
            !RESTRICTED_STORE_TYPES.has(store.type.toUpperCase())
          }
          canDelete={isSuperAdmin}
          isDeleting={deletingStoreId === store.store_id}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )),
    [stores, isSuperAdmin, deletingStoreId, onEdit, onDelete],
  );

  return (
    <TableContainer component={Paper} variant="outlined" sx={tableContainerSx}>
      <Table stickyHeader size="small" sx={{ minWidth: 1180 }}>
        <TableHead>
          <TableRow>
            {TABLE_HEADINGS.map((heading) => (
              <TableCell key={heading} sx={tableHeaderCellSx}>
                {heading}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
    </TableContainer>
  );
});

const StoreCardGrid = memo(function StoreCardGrid({
  stores,
  isSuperAdmin,
  deletingStoreId,
  onEdit,
  onDelete,
}: StoreListProps) {
  const cards = useMemo(
    () =>
      stores.map((store) => (
        <StoreCard
          key={store.store_id}
          store={store}
          canEdit={
            isSuperAdmin ||
            !RESTRICTED_STORE_TYPES.has(store.type.toUpperCase())
          }
          canDelete={isSuperAdmin}
          isDeleting={deletingStoreId === store.store_id}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )),
    [stores, isSuperAdmin, deletingStoreId, onEdit, onDelete],
  );

  return <Box sx={cardGridSx}>{cards}</Box>;
});

interface StoreDialogProps {
  open: boolean;
  initialStore: Store | null;
  saving: boolean;
  fullScreen: boolean;
  onClose: () => void;
  onSave: (store: StoreForm) => Promise<void>;
}

const StoreDialog = memo(function StoreDialog({
  open,
  initialStore,
  saving,
  fullScreen,
  onClose,
  onSave,
}: StoreDialogProps) {
  const [form, setForm] = useState<StoreForm>(EMPTY_STORE);

  useEffect(() => {
    if (!open) return;
    setForm(initialStore ? { ...initialStore } : { ...EMPTY_STORE });
  }, [open, initialStore]);

  const updateField = useCallback((field: keyof Store, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!isStoreFormComplete(form)) {
      alert("Please fill in all required fields.");
      return;
    }

    await onSave(form);
  }, [form, onSave]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      keepMounted={false}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          {initialStore?.store_id ? "Edit Store" : "Add Store"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {initialStore?.store_id
            ? "Update the store information below."
            : "Enter the information for the new store."}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
            pt: 0.5,
          }}
        >
          <TextField
            required
            label="Store Name"
            value={form.name ?? ""}
            onChange={(event) => updateField("name", event.target.value)}
          />
          {/* <TextField
            select
            required
            label="Type"
            value={form.type ?? ""}
            onChange={(event) => updateField("type", event.target.value)}
          >
            {LOCATION_TYPES.map((locationType) => (
              <MenuItem key={locationType} value={locationType}>
                {locationType}
              </MenuItem>
            ))}
          </TextField> */}
          <TextField
            select
            required
            label="Type"
            value={form.type ?? ""}
            onChange={(event) =>
              updateField("type", event.target.value as LocationType)
            }
          >
            {LOCATION_TYPES.map((locationType) => {
              const config = LOCATION_TYPE_CONFIG[locationType];
              const LocationIcon = config.icon;

              return (
                <MenuItem key={locationType} value={locationType}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <LocationIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                    <span>{config.label}</span>
                  </Stack>
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            required
            label="Address"
            value={form.address ?? ""}
            onChange={(event) => updateField("address", event.target.value)}
            sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
          />
          <TextField
            required
            label="City"
            value={form.city ?? ""}
            onChange={(event) => updateField("city", event.target.value)}
          />
          <TextField
            required
            label="Postal Code"
            value={form.postal_code ?? ""}
            onChange={(event) => updateField("postal_code", event.target.value)}
          />
          <TextField
            required
            label="Manager"
            value={form.manager ?? ""}
            onChange={(event) => updateField("manager", event.target.value)}
          />
          <TextField
            required
            type="email"
            label="Email"
            value={form.email ?? ""}
            onChange={(event) => updateField("email", event.target.value)}
          />
          <TextField
            required
            label="Phone"
            value={form.phone ?? ""}
            onChange={(event) => updateField("phone", event.target.value)}
            sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
          "& > :not(style) ~ :not(style)": { ml: 0 },
        }}
      >
        <Button onClick={onClose} disabled={saving} fullWidth={fullScreen}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          fullWidth={fullScreen}
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          {saving ? "Saving..." : "Save Store"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

const Stores: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"), {
    noSsr: true,
  });
  const isDesktopView = useMediaQuery(theme.breakpoints.up("md"), {
    noSsr: true,
  });

  // Select only the role so unrelated auth-state updates do not rerender this page.
  const userRole = useSelector((state: RootState) => state.auth.user?.role);
  const isSuperAdmin = userRole === "SuperAdmin";

  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingStoreId, setDeletingStoreId] = useState<string | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [open, setOpen] = useState(false);

  const storeCountLabel = useMemo(
    () => `${stores.length} ${stores.length === 1 ? "store" : "stores"}`,
    [stores.length],
  );

  const getStoreData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchAllStores();
      setStores(response.data);
    } catch (error) {
      console.error("Error fetching stores:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void getStoreData();
  }, [getStoreData]);

  const handleOpenAdd = useCallback(() => {
    setSelectedStore(null);
    setOpen(true);
  }, []);

  const handleEdit = useCallback((store: Store) => {
    setSelectedStore(store);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (saving) return;
    setOpen(false);
    setSelectedStore(null);
  }, [saving]);

  const handleSaveStore = useCallback(async (storeForm: StoreForm) => {
    try {
      setSaving(true);
      const storePayload = storeForm as Store;

      if (!storeForm.store_id) {
        const response = await addStore(storePayload);

        if (response.status === 200) {
          setStores((current) => [...current, response.data]);
          alert("Store added successfully.");
          setOpen(false);
          setSelectedStore(null);
        }
        return;
      }

      const response = await updateStore(storePayload);

      if (response.status === 200) {
        setStores((current) =>
          current.map((store) =>
            store.store_id === response.data.store_id ? response.data : store,
          ),
        );
        alert("Store updated successfully.");
        setOpen(false);
        setSelectedStore(null);
      }
    } catch (error) {
      console.error("Error saving store:", error);
      alert("Something went wrong while saving the store.");
    } finally {
      setSaving(false);
    }
  }, []);

  const handleDelete = useCallback(async (store: Store) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${store.name}?`,
    );

    if (!confirmed) return;

    try {
      setDeletingStoreId(store.store_id);
      await deleteStore(store.store_id);
      setStores((current) =>
        current.filter((item) => item.store_id !== store.store_id),
      );
      alert("Store deleted successfully.");
    } catch (error) {
      console.error("Error deleting store:", error);
      alert("Something went wrong while deleting the store.");
    } finally {
      setDeletingStoreId(null);
    }
  }, []);

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 2.5 },
        minWidth: 0,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        mb={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Stores
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage store locations and contact information
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent={{ xs: "space-between", sm: "flex-end" }}
        >
          <Chip label={storeCountLabel} size="small" variant="outlined" />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{ whiteSpace: "nowrap" }}
          >
            Add Store
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box
          sx={{
            minHeight: 320,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : stores.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            py: 8,
            px: 2,
            textAlign: "center",
            borderRadius: 2,
          }}
        >
          <StorefrontOutlinedIcon
            sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
          />
          <Typography variant="h6">No stores found</Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Add your first store to get started.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
          >
            Add Store
          </Button>
        </Paper>
      ) : isDesktopView ? (
        <StoreTable
          stores={stores}
          isSuperAdmin={isSuperAdmin}
          deletingStoreId={deletingStoreId}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ) : (
        <StoreCardGrid
          stores={stores}
          isSuperAdmin={isSuperAdmin}
          deletingStoreId={deletingStoreId}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <StoreDialog
        open={open}
        initialStore={selectedStore}
        saving={saving}
        fullScreen={isSmallScreen}
        onClose={handleClose}
        onSave={handleSaveStore}
      />
    </Box>
  );
};

export default Stores;
